import { Injectable } from '@angular/core';
import { TransferState } from '@angular/platform-browser';
import { Entry, EntryCollection } from 'contentful';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseContentfulService } from './base.contentful.service';
import { ContentfulProvider } from './contentful.provider';
import { QueryHelper } from './models/common.query';
import { ContentTypes } from './models/content.types';

@Injectable({ providedIn: 'root' })
export class ContentfulService extends BaseContentfulService {
  constructor(contentfulProvider: ContentfulProvider, state: TransferState) {
    super(contentfulProvider, state);
  }

  protected get fallbackBrand(): string {
    return 'default';
  }

  /** Get a contact page from contentful */
  getPage<T>(
    brand: string,
    slug?: string | null
  ): Observable<Entry<T> | undefined> {
    const query = {
      include: 4,
      ...QueryHelper.slug(slug),
      ...QueryHelper.brand(brand),
    };
    return this.tryLoadEntry<T>(brand, ContentTypes.Page, query);
  }

  /** Get a contact page from contentful */
  getContact<T>(
    brand: string,
    slug?: string | null
  ): Observable<Entry<T> | undefined> {
    const query = {
      include: 4,
      ...QueryHelper.slug(slug),
      ...QueryHelper.brand(brand),
    };
    return this.tryLoadEntry<T>(brand, ContentTypes.ContactUs, query);
  }

  /** Get a help page from contentful */
  getHelp<T>(
    brand: string,
    slug?: string | null
  ): Observable<Entry<T> | undefined> {
    const query = {
      include: 4,
      ...QueryHelper.slug(slug),
      ...QueryHelper.brand(brand),
    };
    return this.tryLoadEntry<T>(brand, ContentTypes.Help, query);
  }

  /** Get the footer entry from contentful */
  getFooter<T>(brand: string): Observable<Entry<T> | undefined> {
    return this.tryLoadEntry<T>(brand, ContentTypes.Footer, {
      include: 4,
      ...QueryHelper.brand(brand),
    });
  }

  /** Get the logo entry from contentful */
  getLogo<T>(brand: string): Observable<Entry<T> | undefined> {
    return this.tryLoadEntry<T>(brand, ContentTypes.Logo, {
      ...QueryHelper.brand(brand),
    });
  }

  getZone<T>(
    contentType: ContentTypes,
    brand: string,
    zone: string,
    fallbackBrand?: string
  ): Observable<Entry<T> | undefined> {
    return this.tryLoadEntries<T>(brand, contentType, {
      include: 4,
      ...QueryHelper.brand(brand, fallbackBrand),
      ...QueryHelper.zone(zone),
    }).pipe(
      map((data) => {
        return this.mapZoneResponse<T>(data, brand, fallbackBrand);
      })
    );
  }

  /**
   * Returns either the current brand entry if defined or the fallback brand
   * @param collection
   * @param brand
   * @param fallbackBrand
   * @private
   */
  private mapZoneResponse<T>(
    collection: EntryCollection<T> | undefined,
    brand: string,
    fallbackBrand?: string
  ): Entry<T> | undefined {
    let result = undefined;

    if (collection && collection.items?.length > 0) {
      if (collection.items.length === 1) {
        result = collection.items[0] as Entry<T>;
      } else {
        const brandEntry = collection.items.find((item) =>
          this.matchesBrand(item, brand)
        );
        result =
          brandEntry ??
          collection.items.find((item) =>
            this.matchesBrand(item, fallbackBrand)
          );
      }
    }

    return result;
  }

  private matchesBrand(entry: Entry<any>, brand?: string): boolean {
    return brand ? entry.fields?.brandCode?.fields?.code === brand : false;
  }
}
